/**
 * assets/geolocation-popup.js
 * EVOC Geolocation Popup controller
 * — /<locale>/ routing, auto-seed from Geo-IP only once, never override manual choice
 *    now with empty-string locale routing to root
 * Enhanced with location-based language filtering and browser-based preselection
 */
(() => {
    const storage = {
        get: (key) => localStorage.getItem(key),
        set: (key, v) => localStorage.setItem(key, v),
    };

    const overlay = document.querySelector("[data-geo-overlay]");
    if (!overlay) return;

    const closeBtn = overlay.querySelector("[data-geo-close]");
    const ctaBtn = overlay.querySelector("[data-geo-cta]");

    // European countries list (ISO 3166-1 alpha-2 codes)
    const EUROPEAN_COUNTRIES = [
        "AD", // Andorra
        "AL", // Albania
        "AT", // Austria
        "BA", // Bosnia and Herzegovina
        "BE", // Belgium
        "BG", // Bulgaria
        "BY", // Belarus
        "CH", // Switzerland (handled separately as it has its own store)
        "CY", // Cyprus
        "CZ", // Czech Republic
        "DE", // Germany (handled separately as it has its own store)
        "DK", // Denmark
        "EE", // Estonia
        "ES", // Spain
        "FI", // Finland
        "FR", // France
        "GB", // United Kingdom
        "GR", // Greece
        "HR", // Croatia
        "HU", // Hungary
        "IE", // Ireland
        "IS", // Iceland
        "IT", // Italy
        "LI", // Liechtenstein
        "LT", // Lithuania
        "LU", // Luxembourg
        "LV", // Latvia
        "MC", // Monaco
        "MD", // Moldova
        "ME", // Montenegro
        "MK", // North Macedonia
        "MT", // Malta
        "NL", // Netherlands
        "NO", // Norway
        "PL", // Poland
        "PT", // Portugal
        "RO", // Romania
        "RS", // Serbia
        "RU", // Russia (European part)
        "SE", // Sweden
        "SI", // Slovenia
        "SK", // Slovakia
        "SM", // San Marino
        "UA", // Ukraine
        "VA", // Vatican City
        "XK", // Kosovo
    ];

    // Country-to-language mapping
    const COUNTRY_LANGUAGE_MAP = {
        US: ["en"], // USA - English only
        CA: ["en"], // Canada - English only
        AU: ["en"], // Australia - English only
        CH: ["de"], // Switzerland - German only
        DE: ["de", "en", "es"], // Germany - All three languages
        EU: ["de", "en", "es"], // European Union - All three languages
    };

    // Default allowed languages (when no specific mapping exists) - now includes Spanish
    const DEFAULT_LANGUAGES = ["de", "en", "es"];

    // Helper functions
    function getBrowserLanguage() {
        const lang = navigator.language || navigator.languages?.[0] || "en";
        const primaryLang = lang.split("-")[0].toLowerCase();
        // Allow de, es explicitly; default to en for any other language
        if (primaryLang === "de" || primaryLang === "es") return primaryLang;
        return "en";
    }

    function getStoredUserLocation() {
        try {
            const storedLocation = localStorage.getItem("userCountryCode");
            return storedLocation ? JSON.parse(storedLocation) : null;
        } catch (error) {
            return null;
        }
    }

    function isEuropeanCountry(countryCode) {
        return EUROPEAN_COUNTRIES.includes(countryCode);
    }

    function mapCountryToAvailableOption(detectedCountry, availableCountries) {
        // If the detected country is directly available, use it
        if (availableCountries.includes(detectedCountry)) {
            return detectedCountry;
        }

        // If it's a European country and EU is available, map to EU
        if (
            isEuropeanCountry(detectedCountry) &&
            availableCountries.includes("EU")
        ) {
            return "EU";
        }

        // Otherwise return null (no mapping found)
        return null;
    }

    function getAllowedLanguagesForCountry(countryCode) {
        const allowed = COUNTRY_LANGUAGE_MAP[countryCode] || DEFAULT_LANGUAGES;

        return allowed;
    }

    function shouldShowLanguageOption(languageCode, allowedLanguages) {
        // Handle empty string locale (root path) - it should respect country restrictions
        if (languageCode === "") {
            // Only show empty string option if English is allowed (since empty string typically means root/English)
            const shouldShow = allowedLanguages.includes("en");

            return shouldShow;
        }
        const shouldShow = allowedLanguages.includes(languageCode);

        return shouldShow;
    }

    // Get browser language every time popup is shown (don't rely on stored values for language)
    const browserLanguage = getBrowserLanguage();

    // For country: ALWAYS prioritize userCountryCode if it exists in available countries
    const storedCountry = storage.get("evocCountry");
    const userCountryCode = getStoredUserLocation(); // This is the detected location

    // Available countries from dropdown
    const countryDropdown = overlay.querySelector("[data-geo-dropdown-country]");
    const availableCountries = Array.from(
        countryDropdown.querySelectorAll(".custom-dropdown__option")
    ).map((option) => option.dataset.value);

    // Priority: userCountryCode (if available) > stored evocCountry > default
    let chosenCountry = null;
    if (userCountryCode) {
        // Try to map the detected country to an available option
        const mappedCountry = mapCountryToAvailableOption(
            userCountryCode,
            availableCountries
        );
        if (mappedCountry) {
            chosenCountry = mappedCountry;
        }
    }

    // If no mapping found, try stored country
    if (
        !chosenCountry &&
        storedCountry &&
        availableCountries.includes(storedCountry)
    ) {
        chosenCountry = storedCountry;
    }

    // Fallback to first available country
    if (!chosenCountry) {
        chosenCountry = availableCountries[0] || null;
    }

    // For language: ALWAYS prioritize browser language, but adjust if not allowed for selected country
    let chosenLanguage = browserLanguage;

    // If we have a chosen country, make sure the browser language is allowed
    if (chosenCountry) {
        const allowedLanguages = getAllowedLanguagesForCountry(chosenCountry);
        if (!allowedLanguages.includes(browserLanguage)) {
            chosenLanguage = allowedLanguages[0];
        }
    }

    function filterLanguageOptions(selectedCountryCode) {
        const langDropdown = overlay.querySelector("[data-geo-dropdown-language]");
        if (!langDropdown) return;

        const allowedLanguages = getAllowedLanguagesForCountry(selectedCountryCode);
        const languageOptions = langDropdown.querySelectorAll(
            ".custom-dropdown__option"
        );

        let visibleOptions = [];
        let hasSelectedVisible = false;

        languageOptions.forEach((option) => {
            const languageCode = option.dataset.value;
            const shouldShow = shouldShowLanguageOption(
                languageCode,
                allowedLanguages
            );

            if (shouldShow) {
                option.style.display = "block";
                visibleOptions.push(option);
                if (option.classList.contains("is-selected")) {
                    hasSelectedVisible = true;
                }
            } else {
                option.style.display = "none";
                option.classList.remove("is-selected");
            }
        });

        // If currently selected language is not visible, select the first visible option
        if (!hasSelectedVisible && visibleOptions.length > 0) {
            const firstVisible = visibleOptions[0];
            languageOptions.forEach((opt) => opt.classList.remove("is-selected"));
            firstVisible.classList.add("is-selected");

            // Update the dropdown display
            const valueEl = langDropdown.querySelector(".custom-dropdown__value");
            valueEl.textContent = firstVisible.textContent.trim();

            // Update chosen language
            chosenLanguage = firstVisible.dataset.value;
        }
    }

    function initDropdown(root, key, defaultValue) {
        const trigger = root.querySelector("[data-geo-dropdown-trigger]");
        const valueEl = root.querySelector(".custom-dropdown__value");
        const list = root.querySelector("[data-geo-dropdown-options]");
        const items = Array.from(list.children);

        function select(item, isInitialization = false) {
            items.forEach((i) => i.classList.remove("is-selected"));
            item.classList.add("is-selected");
            valueEl.textContent = item.textContent.trim();

            if (key === "country") {
                chosenCountry = item.dataset.value;
                storage.set("evocCountry", chosenCountry);

                // Only set manual flag if this is not initialization
                if (!isInitialization) {
                    storage.set("evocCountryManual", "true"); // Mark as manual selection
                }

                // Filter language options based on selected country
                filterLanguageOptions(chosenCountry);
            } else {
                chosenLanguage = item.dataset.value; // may be ""
            }

            root.classList.remove("open");
            trigger.setAttribute("aria-expanded", "false");
        }

        // preselect stored or default
        const initValue =
            (key === "country" ? chosenCountry : chosenLanguage) || defaultValue;
        const pre = items.find((i) => i.dataset.value === initValue) || items[0];

        select(pre, true); // true = this is initialization, not manual selection

        // toggle
        trigger.addEventListener("click", () => {
            const isOpen = root.classList.toggle("open");
            trigger.setAttribute("aria-expanded", isOpen);
            if (isOpen) {
                // Focus first visible option
                const firstVisible = Array.from(items).find(
                    (item) => item.style.display !== "none"
                );
                if (firstVisible) firstVisible.focus();
            }
        });

        // click outside
        document.addEventListener("click", (e) => {
            if (!root.contains(e.target)) {
                root.classList.remove("open");
                trigger.setAttribute("aria-expanded", "false");
            }
        });

        // option events
        items.forEach((item) => {
            item.tabIndex = 0;
            item.addEventListener("click", () => {
                // Only allow selection if option is visible
                if (item.style.display !== "none") {
                    select(item);
                }
            });
            item.addEventListener("keydown", (e) => {
                if (
                    (e.key === "Enter" || e.key === " ") &&
                    item.style.display !== "none"
                ) {
                    e.preventDefault();
                    select(item);
                }
            });
        });

        return {select};
    }

    // init country dropdown
    let countryDropdownApi = null;
    if (countryDropdown) {
        const firstCountry = countryDropdown.querySelector(
            ".custom-dropdown__option"
        ).dataset.value;
        countryDropdownApi = initDropdown(countryDropdown, "country", firstCountry);
    }

    // init language dropdown
    const langDropdown = overlay.querySelector("[data-geo-dropdown-language]");
    if (langDropdown) {
        const firstLang = langDropdown.querySelector(".custom-dropdown__option")
            .dataset.value;
        initDropdown(langDropdown, "language", firstLang);

        // Apply initial filtering if country is already selected
        if (chosenCountry) {
            filterLanguageOptions(chosenCountry);
        }
    }

    // On a first-ever visit locator.js hasn't resolved the geo-IP lookup yet when
    // this script runs, so the preselection above ran without a detected country.
    // Re-apply it once locator.js dispatches the result, unless the user already
    // picked a country by hand in the meantime.
    window.addEventListener("userLocationDetected", (event) => {
        if (storage.get("evocCountryManual") === "true" || !countryDropdownApi) return;

        const detected = event.detail;
        if (!detected || detected === "DEFAULT") return;

        const mapped = mapCountryToAvailableOption(detected, availableCountries);
        if (!mapped || mapped === chosenCountry) return;

        const target = countryDropdown.querySelector(`[data-value="${mapped}"]`);
        if (target) {
            countryDropdownApi.select(target, true);
        }
    });

    // hide + persist
    function hidePopup() {
        overlay.style.display = "none";
        storage.set("geoPopupDismissed", "true");
    }

    // figure out current locale from path
    function getCurrentLocale() {
        const segs = window.location.pathname.split("/").filter(Boolean);
        if (segs[0] && /^[A-Za-z]{2}$/.test(segs[0])) return segs[0];
        // If no language prefix, we're on the root path (English)
        return "";
    }

    // "Ride on" button
    ctaBtn?.addEventListener("click", () => {
        // 1) External country redirect (US, CA, CH, AU) - always redirect to homepage
        if (chosenCountry) {
            const opt = countryDropdown.querySelector(
                `[data-value='${chosenCountry}']`
            );
            const url = opt?.dataset.redirect;
            if (url) {
                storage.set("geoPopupDismissed", "true");
                window.location.href = url;
                return;
            }
        }

        // 2) EU/DE store language handling - stay on current page
        if (chosenLanguage !== null) {
            const current = getCurrentLocale();

            // Check if language is already correct - if so, just close popup
            if (chosenLanguage === current) {
                hidePopup();
                return;
            }

            // Language switch needed - preserve current path for EU/DE
            if (chosenLanguage === "") {
                // Switch to English (root path) - preserve current page path
                let currentPath = window.location.pathname;
                // Remove existing language prefix if present
                let segs = currentPath.split("/").filter(Boolean);
                if (segs[0] && /^[A-Za-z]{2}$/.test(segs[0])) {
                    segs.shift();
                }
                const newPath = "/" + segs.join("/");
                const qs = window.location.search || "";
                const hash = window.location.hash || "";
                storage.set("geoPopupDismissed", "true");
                window.location.href = window.location.origin + newPath + qs + hash;
                return;
            } else {
                // Switch to German - preserve current page path
                let currentPath = window.location.pathname;
                let segs = currentPath.split("/").filter(Boolean);
                // Remove existing language prefix if present
                if (segs[0] && /^[A-Za-z]{2}$/.test(segs[0])) {
                    segs.shift();
                }
                // Add new language prefix
                segs.unshift(chosenLanguage);
                const newPath = "/" + segs.join("/");
                const qs = window.location.search || "";
                const hash = window.location.hash || "";
                storage.set("geoPopupDismissed", "true");
                window.location.href = window.location.origin + newPath + qs + hash;
                return;
            }
        }

        // 3) Otherwise just close
        hidePopup();
    });

    // "X" close: only hide for this session
    closeBtn?.addEventListener("click", () => {
        sessionStorage.setItem("geoPopupClosed", "true");
        overlay.style.display = "none";
    });

    // only show once
    document.addEventListener("DOMContentLoaded", () => {
        const dismissedPersist = storage.get("geoPopupDismissed") === "true";
        const closedSession = sessionStorage.getItem("geoPopupClosed") === "true";
        if (!dismissedPersist && !closedSession) {
            overlay.style.display = "flex";
        }
    });
})();
